import { Component, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, X, Copy, Download, RefreshCw, AlertTriangle, Sparkles, Camera, Lightbulb, Undo2, BadgeCheck } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { ProductLinkImport } from "../components/ProductLinkImport";
import { BrandExtract } from "../components/BrandExtract";
import api from "../lib/api";

const AUTO_STAGE_MAP = {
  understanding_brand: "Understanding your brand",
  reading_product: "Reading your product",
  collecting_references: "Collecting visual references",
  building_directions: "Building creative directions",
  generating_1_of_4: "Generating creative 1 of 4",
  generating_2_of_4: "Generating creative 2 of 4",
  generating_3_of_4: "Generating creative 3 of 4",
  generating_4_of_4: "Generating creative 4 of 4",
  validating_1_of_4: "Checking creative 1 of 4",
  validating_2_of_4: "Checking creative 2 of 4",
  validating_3_of_4: "Checking creative 3 of 4",
  validating_4_of_4: "Checking creative 4 of 4",
  preparing_set: "Preparing your creative set",
  completed: "Ready", partial: "Ready (partial)", failed: "Something went wrong",
};

const AUTO_ACTIVE_STATUSES = new Set(["queued", "planning", "generating"]);
const AUTO_TERMINAL_STATUSES = new Set(["completed", "partial", "failed"]);

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OBJECTIVES = [
  { value: "sales", label: "Sales" },
  { value: "leads", label: "Leads" },
  { value: "awareness", label: "Awareness" },
];

const imgUrl = (setId, v, fmt, download = false, vid = "") =>
  `${API_BASE}/creative-studio/image/${setId}/${v}/${fmt}?download=${download ? 1 : 0}${vid ? `&v=${vid}` : ""}`;

const SectionLabel = ({ children, className = "" }) => (
  <p className={`text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase ${className}`}>{children}</p>
);

// Normalize a creative variant across schema generations:
// - legacy (fields on variant root)
// - auto_ai (execution_spec.*)
// - transitional (expected_text.* or copy.*)
const normalizeCreativeVariant = (variant, idx = 0) => {
  const v = variant && typeof variant === "object" ? variant : {};
  const spec = (v.execution_spec && typeof v.execution_spec === "object") ? v.execution_spec : {};
  const expected = (v.expected_text && typeof v.expected_text === "object") ? v.expected_text : {};
  const copy = (v.copy && typeof v.copy === "object") ? v.copy : {};
  const pick = (...vals) => {
    for (const x of vals) if (typeof x === "string" && x.length > 0) return x;
    return "";
  };
  return {
    headline: pick(spec.headline, expected.headline, copy.headline, v.headline),
    subhead: pick(spec.subhead, expected.subhead, copy.subhead, v.subhead),
    cta: pick(spec.cta, expected.cta, copy.cta, v.cta),
    label: pick(spec.label, v.label),
    body: pick(spec.body, copy.body, v.body),
    caption: pick(v.caption, copy.caption, spec.caption),
    hashtags: Array.isArray(v.hashtags) ? v.hashtags : [],
    mode: pick(v.mode, spec.mode),
    concept_id: pick(v.concept_id, v.id) || `variant_${idx}`,
    raw: v,
  };
};

// Minimal local error boundary — used only around the Gallery so an
// unexpected render error never blanks the whole /app/performance/creative page.
class GalleryErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) {
    try { console.error("[creative-gallery] render error", err); } catch (_) { /* noop */ }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="card-soft mt-6 p-6 text-center" data-testid="creative-gallery-error">
          <p className="text-[13.5px] font-bold text-[#181621]">We couldn&apos;t display this creative set.</p>
          <p className="text-[12px] text-[#54505B] mt-1">Return to Creative Studio and try another set.</p>
          <button
            onClick={() => { try { this.props.onReset && this.props.onReset(); } catch (_) { /* noop */ } this.setState({ hasError: false }); }}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12.5px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors"
            data-testid="creative-gallery-error-reset">
            Back to Creative Studio
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const Gallery = ({ set, quotaThirdsLeft, onRegen, onUndo, busy }) => {
  const [active, setActive] = useState(0);
  const [fmt, setFmt] = useState("feed");
  // Reset selection whenever the incoming set identity changes.
  useEffect(() => { setActive(0); }, [set?.id, set?.active_vid]);

  const rawVariants = Array.isArray(set?.variants) ? set.variants.filter(Boolean) : [];
  const variants = rawVariants.map((vv, i) => normalizeCreativeVariant(vv, i));
  const versions = Array.isArray(set?.versions) ? set.versions : [];
  const status = set?.status || (variants.length > 0 ? "ready" : "unknown");

  // Empty / partial / failed states — never touch .headline on undefined.
  if (!set) {
    return (
      <div className="card-soft mt-4 p-6 text-center" data-testid="creative-gallery-empty">
        <p className="text-[12.5px] text-[#54505B]">Loading your creative set…</p>
      </div>
    );
  }
  if (variants.length === 0) {
    const isFailed = status === "failed" || set?.error;
    const isProcessing = ["pending", "generating", "processing", "queued"].includes(status);
    return (
      <div className="card-soft mt-4 p-6 text-center" data-testid="creative-gallery-empty">
        {isProcessing ? (
          <p className="text-[12.5px] text-[#54505B]">Your creatives are still being generated…</p>
        ) : isFailed ? (
          <>
            <p className="text-[13.5px] font-bold text-[#181621]">This set couldn&apos;t be produced.</p>
            <p className="text-[12px] text-[#54505B] mt-1">No creatives were saved. Please start a new set.</p>
          </>
        ) : (
          <p className="text-[12.5px] text-[#54505B]">No creatives available for this set.</p>
        )}
      </div>
    );
  }

  const safeIndex = Math.min(Math.max(active, 0), variants.length - 1);
  const v = variants[safeIndex] || variants[0];
  const vid = set.active_vid || "";
  const activeVersionIdx = versions.findIndex((x) => x && x.vid === set.active_vid);
  const copyCaption = () => {
    const caption = v.caption || "";
    const tags = (v.hashtags || []).map((h) => `#${h}`).join(" ");
    navigator.clipboard.writeText(`${caption}\n\n${tags}`)
      .then(() => toast("Caption copied.", { description: "Paste it straight into your ad." }))
      .catch(() => toast.error("Copy failed — select and copy the caption manually."));
  };
  return (
    <div className="grid lg:grid-cols-[1fr_260px] gap-5 mt-4" data-testid="creative-gallery">
      <div className="card-soft overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-2 border-b border-[#F3EFE7] flex-wrap">
          <div className="inline-flex rounded-lg border border-[#EAE5DB] overflow-hidden">
            {["feed", "story"].map((ft, i) => (
              <button key={ft} onClick={() => setFmt(ft)}
                className={`px-3 py-1.5 text-[11px] font-bold tabular-nums transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${fmt === ft ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
                data-testid={`gallery-fmt-${ft}`}>
                {ft === "feed" ? "Feed 4:5" : "Story 9:16"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {versions.length > 1 && activeVersionIdx > 0 && (
              <button onClick={onUndo} className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-2.5 py-1 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors" data-testid="gallery-undo-btn">
                <Undo2 size={12} strokeWidth={2} /> Undo
              </button>
            )}
            <a href={imgUrl(set.id, safeIndex, fmt, true, vid)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-2.5 py-1 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors" data-testid="gallery-download-btn">
              <Download size={12} strokeWidth={2} /> PNG
            </a>
          </div>
        </div>
        <div className="bg-[#F3EFE7] flex justify-center py-3">
          <img src={imgUrl(set.id, safeIndex, fmt, false, vid)} alt={v.headline || `Creative ${safeIndex + 1}`}
            className={fmt === "feed" ? "w-full max-w-[560px] rounded-lg" : "h-[560px] rounded-lg"}
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.fallback === "1") return;
              el.dataset.fallback = "1";
              el.replaceWith(Object.assign(document.createElement("div"), {
                className: (fmt === "feed" ? "w-full max-w-[560px] " : "h-[560px] ") + "rounded-lg bg-[#EAE5DB] text-[#54505B] text-[12px] font-semibold flex items-center justify-center aspect-square",
                textContent: "Creative preview unavailable",
              }));
            }}
            data-testid="gallery-preview-image" />
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[13px] text-[#181621] leading-snug whitespace-pre-line flex-1" data-testid="gallery-caption">{v.caption || v.headline || ""}</p>
            <button onClick={copyCaption} className="text-[#54505B] hover:text-[#181621] shrink-0" data-testid="gallery-copy-caption">
              <Copy size={15} strokeWidth={1.8} />
            </button>
          </div>
          <p className="text-[11.5px] text-[#0B67FF] font-semibold mt-2 leading-snug" data-testid="gallery-hashtags">
            {(v.hashtags || []).map((h) => `#${h}`).join(" ")}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {variants.map((vv, i) => (
          <button key={vv.concept_id || i} onClick={() => setActive(i)}
            className={`w-full rounded-xl border overflow-hidden text-left transition-colors bg-white ${safeIndex === i ? "border-[#181621] shadow-sm" : "border-[#EAE5DB] hover:border-[#9A9591]"}`}
            data-testid={`gallery-thumb-${i}`}>
            <div className="flex items-center gap-3 p-2">
              <img src={imgUrl(set.id, i, "feed", false, vid)} alt="" className="w-16 h-16 rounded-lg object-cover bg-[#EAE5DB]"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
              <div className="min-w-0">
                <p className="text-[9.5px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">{vv.mode || `Variant ${i + 1}`}</p>
                <p className="text-[12px] font-bold text-[#181621] truncate">{vv.headline || `Creative ${i + 1}`}</p>
              </div>
            </div>
          </button>
        ))}
        {versions.length > 0 && (
          <SecondaryButton className="w-full !py-2 !text-[12px] flex items-center justify-center gap-1.5"
            onClick={() => onRegen(safeIndex)} disabled={busy || quotaThirdsLeft < 1} data-testid="gallery-regen-btn">
            <RefreshCw size={12} strokeWidth={1.8} /> Re-shoot this variant · ⅓ set
          </SecondaryButton>
        )}
        {versions.length > 0 && (
          <p className="text-[10.5px] font-semibold text-[#9A9591] text-center tabular-nums" data-testid="gallery-version-label">
            Version {activeVersionIdx + 1} of {versions.length} · last 3 kept
          </p>
        )}
      </div>
    </div>
  );
};

const RefineChat = ({ set, onSend, busy }) => {
  const [msg, setMsg] = useState("");
  const [remember, setRemember] = useState(false);
  const [savedHint, setSavedHint] = useState(false);
  const messages = set.refine_messages || [];
  const userCount = messages.filter((m) => m.role === "user").length;
  const left = 10 - userCount;
  const send = () => {
    const t = msg.trim();
    if (t.length < 2) return;
    setMsg("");
    onSend(t, remember);
    if (remember) {
      setSavedHint(true);
      setTimeout(() => setSavedHint(false), 3500);
      setRemember(false);
    }
  };
  return (
    <div className="card-soft mt-5 p-5" data-testid="creative-refine-chat">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <SectionLabel>Refine with AI — talk to your art director</SectionLabel>
        <span className="text-[10.5px] font-semibold text-[#9A9591] tabular-nums" data-testid="refine-messages-left">{left} messages left on this set</span>
      </div>
      {messages.length > 0 && (
        <div className="mt-4 space-y-2.5 max-h-64 overflow-y-auto pr-1" data-testid="refine-messages-list">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <p className={`rounded-2xl px-3.5 py-2 text-[12.5px] leading-snug max-w-[85%] ${m.role === "user" ? "bg-[#181621] text-white" : "bg-[#FAF8F3] border border-[#F3EFE7] text-[#181621]"}`}
                data-testid={`refine-msg-${m.role}-${i}`}>
                {m.content}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2.5 mt-4">
        <input className="field-input !py-2.5 flex-1" placeholder='e.g. "warmer light", "stronger headline on variant 2", "more premium mood"'
          value={msg} onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
          disabled={busy || left <= 0} data-testid="refine-chat-input" />
        <CtaButton className="!py-2.5 !px-4 text-[13px]" onClick={send} disabled={busy || left <= 0 || msg.trim().length < 2} data-testid="refine-chat-send">
          <Sparkles size={13} strokeWidth={1.8} /> Refine
        </CtaButton>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
        <label className="inline-flex items-center gap-2 text-[11.5px] text-[#54505B] select-none cursor-pointer" data-testid="creative-remember-label">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#181621]" data-testid="creative-remember-toggle" />
          Remember this instruction for future outputs
        </label>
        {savedHint && (
          <span className="text-[11px] text-[#181621] font-semibold" data-testid="creative-remember-saved">
            Saved to your brand memory.
          </span>
        )}
      </div>
      <p className="text-[10.5px] text-[#9A9591] mt-2">Image re-shoots count ⅓ of a set each · copy-only changes are free · last 3 versions kept with undo.</p>
    </div>
  );
};

export default function CreativeStudio() {
  const location = useLocation();
  const [usage, setUsage] = useState(null);
  const [kit, setKit] = useState(undefined);
  const [inspiration, setInspiration] = useState(location.state?.inspiration || null);
  const [photo, setPhoto] = useState(null); // {upload_id, preview}
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({ product_desc: "", offer: "", target_notes: "", objective: "sales" });
  const [phase, setPhase] = useState("idle"); // idle | generating | error
  const [stage, setStage] = useState("");
  const [autoCaps, setAutoCaps] = useState(null);
  const [autoSet, setAutoSet] = useState(null);
  const [busyAuto, setBusyAuto] = useState(false);
  const [autoCardBusy, setAutoCardBusy] = useState({});     // {concept_id: "refine"|"regen"|"undo"}
  const [autoCardHint, setAutoCardHint] = useState({});     // {concept_id: "Saved…"}
  const [autoRefineText, setAutoRefineText] = useState({}); // {concept_id: "text"}
  const [autoRefineRemember, setAutoRefineRemember] = useState({});
  const [autoImgVer, setAutoImgVer] = useState({});         // cache-busting per card
  const [autoCardFmt, setAutoCardFmt] = useState({});       // {concept_id: "feed"|"story"}
  const autoActionToken = useRef(0);

  const _autoAction = async (kind, concept_id, body) => {
    if (autoCardBusy[concept_id]) return;
    const token = ++autoActionToken.current;
    setAutoCardBusy((s) => ({ ...s, [concept_id]: kind }));
    try {
      const url = `/creative-studio/auto-sets/${autoSet.set_id}/${kind}`;
      const { data } = await api.post(url, body);
      if (token !== autoActionToken.current) return;  // stale
      setAutoSet(data);
      setAutoImgVer((s) => ({ ...s, [concept_id]: (s[concept_id] || 0) + 1 }));
      if (kind === "refine" && body.remember_instruction) {
        setAutoCardHint((s) => ({ ...s, [concept_id]: "Saved to your brand memory." }));
        setTimeout(() => setAutoCardHint((s) => ({ ...s, [concept_id]: null })), 3500);
      }
      if (kind === "refine") {
        setAutoRefineText((s) => ({ ...s, [concept_id]: "" }));
        setAutoRefineRemember((s) => ({ ...s, [concept_id]: false }));
      }
    } catch (err) {
      if (err.response?.status === 402) { window.dispatchEvent(new CustomEvent("agenzee:upgrade")); return; }
      toast.error(err.response?.data?.detail || "Couldn't apply the change.");
    } finally {
      setAutoCardBusy((s) => ({ ...s, [concept_id]: null }));
    }
  };
  const autoRefine = (cid) => _autoAction("refine", cid, {
    concept_id: cid,
    message: (autoRefineText[cid] || "").trim(),
    remember_instruction: !!autoRefineRemember[cid],
  });
  const autoRegen = (cid) => _autoAction("regenerate", cid, { concept_id: cid });
  const autoUndoAction = (cid) => _autoAction("undo", cid, { concept_id: cid });
  useEffect(() => {
    api.get("/creative-studio/auto-capabilities").then(({ data }) => setAutoCaps(data))
      .catch(() => setAutoCaps({ enabled: false }));
  }, []);
  const [errMsg, setErrMsg] = useState("");
  const [sets, setSets] = useState([]);
  const [activeSet, setActiveSet] = useState(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  const loadUsage = () => api.get("/creative-studio/usage").then(({ data }) => setUsage(data)).catch(() => {});
  const loadSets = async (keepId = null) => {
    const { data } = await api.get("/creative-studio/sets");
    setSets(data);
    if (keepId) {
      const found = data.find((s) => s.id === keepId);
      setActiveSet(found || data[0] || null);
    } else if (data.length) {
      setActiveSet(data[0]);
    }
    return data;
  };

  const autoPollRef = useRef(null);
  const stopAutoPolling = () => { if (autoPollRef.current) clearInterval(autoPollRef.current); autoPollRef.current = null; };
  const startAutoPolling = (sid) => {
    stopAutoPolling();
    let ticks = 0;
    autoPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 300) { stopAutoPolling(); setPhase("error"); setErrMsg("The shoot took too long. Try again."); setBusyAuto(false); return; }
      try {
        const { data: st } = await api.get(`/creative-studio/auto-status/${sid}`);
        setStage(AUTO_STAGE_MAP[st.stage] || "Working…");
        setAutoSet(st);
        if (AUTO_TERMINAL_STATUSES.has(st.status)) {
          stopAutoPolling();
          setPhase("idle");
          setBusyAuto(false);
          if (st.status === "failed" && !(st.creatives || []).some((c) => c.download_available)) {
            setErrMsg(st.error || "The shoot went wrong. Try again.");
            setPhase("error");
          }
        }
      } catch (_e) {
        stopAutoPolling();
        setPhase("idle"); setBusyAuto(false);
      }
    }, 2500);
  };

  useEffect(() => {
    loadUsage();
    loadSets().catch(() => {});
    api.get("/brand-brain").then(({ data }) => setKit(data.brand_kit || {})).catch(() => setKit({}));
    // Legacy status resume (3-variant pipeline)
    api.get("/creative-studio/status").then(({ data }) => {
      if (data.status === "pending") { setPhase("generating"); setStage(data.stage || ""); startPolling(data.set_id || null); }
    }).catch(() => {});
    // Auto v3 resume: if an auto set is still running, resume polling it
    api.get("/creative-studio/auto-latest").then(({ data }) => {
      if (data && data.set_id) {
        setAutoSet(data);
        if (AUTO_ACTIVE_STATUSES.has(data.status)) {
          setPhase("generating");
          setStage(AUTO_STAGE_MAP[data.stage] || "Working…");
          setBusyAuto(true);
          startAutoPolling(data.set_id);
        }
      }
    }).catch(() => {});
    return () => { stopPolling(); stopAutoPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = (keepSetId = null) => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 300) { stopPolling(); setPhase("error"); setErrMsg("The shoot took too long. Try again."); return; }
      try {
        const { data } = await api.get("/creative-studio/status");
        if (data.status === "failed") {
          stopPolling(); setPhase("error"); setErrMsg(data.error || "The shoot went wrong. Try again.");
          if (keepSetId) loadSets(keepSetId).catch(() => {});
        } else if (data.status === "complete") {
          stopPolling();
          await loadSets(keepSetId);
          loadUsage();
          setPhase("idle");
          toast(keepSetId ? "Set updated." : "3 creatives ready.", { description: keepSetId ? "New version saved — undo anytime." : "Feed and Story formats, captions included." });
        } else {
          setStage(data.stage || "");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/creative-studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto({ upload_id: data.upload_id, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  const generate = async (refineInstructions = null) => {
    setPhase("generating");
    setStage("Writing the creative brief…");
    setErrMsg("");
    try {
      await api.post("/creative-studio/generate", {
        ...f,
        upload_id: photo?.upload_id || null,
        refine_instructions: refineInstructions,
        inspiration: inspiration || null,
      });
      startPolling();
    } catch (err) {
      setPhase("idle");
      if (err.response?.status === 402) { window.dispatchEvent(new CustomEvent("agenzee:upgrade")); return; }
      toast.error(err.response?.data?.detail || "Couldn't start the shoot.");
    }
  };

  const generateAuto = async () => {
    if (busyAuto) return;
    setBusyAuto(true);
    setPhase("generating");
    setStage(AUTO_STAGE_MAP.understanding_brand);
    setErrMsg("");
    setAutoSet({ set_id: null, status: "queued", stage: "understanding_brand",
                 creatives: [], completed_count: 0, failed_count: 0,
                 total: 4 });
    const idempotencyKey = (crypto.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now();
    try {
      const { data } = await api.post("/creative-studio/auto-generate", {
        product_description: f.product_desc || null,
        offer: f.offer || null,
        target_notes: f.target_notes || null,
        objective: f.objective,
        upload_id: photo?.upload_id || null,
        product_url: null,
        reference_ids: [],
        idempotency_key: idempotencyKey,
      });
      startAutoPolling(data.set_id);
    } catch (err) {
      setPhase("idle");
      setBusyAuto(false);
      setAutoSet(null);
      if (err.response?.status === 402) { window.dispatchEvent(new CustomEvent("agenzee:upgrade")); return; }
      toast.error(err.response?.data?.detail || "Couldn't start the shoot.");
    }
  };

  const regenVariant = async (variantIdx) => {
    setPhase("generating");
    setStage(`Re-shooting variant ${variantIdx + 1}…`);
    setErrMsg("");
    try {
      await api.post(`/creative-studio/sets/${activeSet.id}/regenerate`, { variant: variantIdx });
      startPolling(activeSet.id);
    } catch (err) {
      setPhase("idle");
      toast.error(err.response?.data?.detail || "Couldn't start the re-shoot.");
    }
  };

  const refineSet = async (message) => {
    const sid = activeSet.id;
    setActiveSet((s) => ({ ...s, refine_messages: [...(s.refine_messages || []), { role: "user", content: message }] }));
    setPhase("generating");
    setStage("Reading your notes…");
    setErrMsg("");
    try {
      await api.post(`/creative-studio/sets/${sid}/refine`, { message });
      startPolling(sid);
    } catch (err) {
      setPhase("idle");
      loadSets(sid).catch(() => {});
      toast.error(err.response?.data?.detail || "Couldn't start the refinement.");
    }
  };

  const undoSet = async () => {
    try {
      await api.post(`/creative-studio/sets/${activeSet.id}/undo`);
      await loadSets(activeSet.id);
      toast("Reverted to the previous version.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nothing to undo.");
    }
  };

  const canGenerate = (f.offer.trim().length >= 2) && (photo || f.product_desc.trim());
  const quotaLeft = usage ? Math.round((usage.limit_units - usage.units) / 3 * 10) / 10 : null;
  const thirdsLeft = usage ? usage.limit_units - usage.units : 0;

  return (
    <div className="p-6 sm:p-10 max-w-4xl" data-testid="creative-studio-page">
      <MonoLabel>Performance Studio · Creative Studio</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Ad creatives, shot and designed
      </h1>
      {usage && (
        <div className="mt-5 max-w-sm" data-testid="creative-usage-counter">
          <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${usage.limit ? Math.min(100, ((usage.limit - quotaLeft) / usage.limit) * 100) : 0}%`,
                background: quotaLeft <= 0 ? "var(--rose-fg)" : quotaLeft <= 2 ? "var(--amber-fg)" : "linear-gradient(90deg, #E0603A, #0B67FF)",
              }}
            />
          </div>
          <p className={`text-[11.5px] font-semibold mt-1.5 tabular-nums ${quotaLeft <= 0 ? "text-[#B4574A]" : quotaLeft <= 2 ? "text-[#96691E]" : "text-[#54505B]"}`}>
            {quotaLeft} of {usage.limit} sets left this month
          </p>
        </div>
      )}

      {kit !== undefined && (
        (kit.primary_color || kit.secondary_color || kit.typography || kit.has_logo) ? (
          <div className="inline-flex items-center flex-wrap gap-2.5 mt-4 rounded-xl border border-[#EAE5DB] bg-white px-3.5 py-2 shadow-sm" data-testid="creative-onbrand-badge">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#181621] uppercase tracking-[0.06em]">
              <BadgeCheck size={13} strokeWidth={2} className="text-[#0B67FF]" /> On-brand mode
            </span>
            <span className="flex items-center gap-1">
              {kit.primary_color && <span className="w-4 h-4 rounded-full border border-[#EAE5DB]" style={{ background: kit.primary_color }} data-testid="onbrand-primary-swatch" />}
              {kit.secondary_color && <span className="w-4 h-4 rounded-full border border-[#EAE5DB]" style={{ background: kit.secondary_color }} data-testid="onbrand-secondary-swatch" />}
            </span>
            <span className="text-[12px] text-[#54505B]">
              Your Brand Kit colors{kit.has_logo ? ", logo" : ""}{kit.typography ? " and type" : ""} are applied automatically.
            </span>
          </div>
        ) : (
          <div className="mt-4" data-testid="creative-no-kit-block">
            <p className="text-[12px] text-[#54505B]" data-testid="creative-no-kit-hint">
              No Brand Kit yet — <Link to="/app/settings/brand-brain" className="underline font-semibold text-[#181621]">set up colors, logo and type</Link> so every creative comes out on-brand.
            </p>
            <div className="mt-3 max-w-xl">
              <BrandExtract compact onDone={() => api.get("/brand-brain").then(({ data }) => setKit(data.brand_kit || {})).catch(() => {})} />
            </div>
          </div>
        )
      )}

      <div className="card-soft mt-8 p-6 sm:p-8" data-testid="creative-input-card">
        {inspiration && (
          <div className="card-soft !shadow-none border-l-4 border-l-[#E0603A] p-4 mb-6 flex items-start gap-3" data-testid="creative-inspiration-banner">
            <span className="w-8 h-8 rounded-lg bg-[#FDF1EC] border border-[#F2D8CE] flex items-center justify-center shrink-0">
              <Lightbulb size={15} strokeWidth={1.8} className="text-[#E0603A]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold tracking-[0.1em] text-[#B4552D] uppercase">Inspired by: {inspiration.angle_summary}</p>
              <p className="text-[12px] text-[#54505B] mt-1 leading-snug">
                {inspiration.hook_mechanics} Your creatives will build on this angle with YOUR product and brand — nothing is copied.
              </p>
            </div>
            <button onClick={() => setInspiration(null)} className="text-[#9A9591] hover:text-[#181621] shrink-0" data-testid="creative-inspiration-dismiss">
              <X size={15} strokeWidth={2} />
            </button>
          </div>
        )}
        <div className="mb-6 pb-6 border-b border-[#F3EFE7]">
          <ProductLinkImport
            target="creative"
            onApply={(p) => setF((prev) => ({
              ...prev,
              product_desc: [p.name, p.description].filter(Boolean).join(" — ").slice(0, 400) || prev.product_desc,
              offer: p.price_text || prev.offer,
            }))}
            onImage={(data, src) => setPhoto({ upload_id: data.upload_id, preview: src })}
          />
        </div>
        <div className="grid sm:grid-cols-[200px_1fr] gap-6">
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-2">Product photo (preferred)</label>
            {photo ? (
              <div className="relative" data-testid="photo-preview">
                <img src={photo.preview} alt="product" className="w-full h-40 object-contain rounded-xl bg-[#F3EFE7] border border-[#EAE5DB]" />
                <button
                  onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 bg-[#181621] text-white rounded-full p-1"
                  data-testid="photo-remove-btn"
                >
                  <X size={13} strokeWidth={2.2} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full h-40 rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] hover:border-[#181621] transition-colors flex flex-col items-center justify-center gap-2 text-[#54505B] shadow-sm"
                data-testid="photo-upload-btn"
              >
                <span className="w-9 h-9 rounded-lg bg-white border border-[#EAE5DB] flex items-center justify-center">
                  <ImagePlus size={17} strokeWidth={1.6} />
                </span>
                <span className="text-[12px] font-bold text-[#181621]">{uploading ? "Uploading…" : "Upload photo"}</span>
                <span className="text-[10.5px] text-[#9A9591]">Your product stays untouched</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="photo-file-input" />
            <p className="mt-2 text-[10.5px] text-[#9A9591] leading-snug" data-testid="creative-optional-assets-hint">
              Optional — add a logo or product image for more accurate results.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">{photo ? "Product notes (optional)" : "Or describe the product"}</label>
              <textarea rows={2} className="field-input resize-none" placeholder='e.g. "Handmade leather tote, cognac brown, brass details"' value={f.product_desc} onChange={(e) => setF({ ...f, product_desc: e.target.value })} data-testid="product-desc-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">This campaign's offer</label>
              <input className="field-input" placeholder='e.g. "-30% until Sunday" or "Free first consultation"' value={f.offer} onChange={(e) => setF({ ...f, offer: e.target.value })} data-testid="offer-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Target notes (optional)</label>
                <input className="field-input" placeholder="Brand Brain snapshot is auto-included" value={f.target_notes} onChange={(e) => setF({ ...f, target_notes: e.target.value })} data-testid="target-notes-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Objective</label>
                <div className="inline-flex rounded-xl border border-[#EAE5DB] bg-white overflow-hidden">
                  {OBJECTIVES.map((o, i) => (
                    <button key={o.value} onClick={() => setF({ ...f, objective: o.value })} className={`px-4 py-2 text-[12.5px] font-bold transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${f.objective === o.value ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`} data-testid={`objective-${o.value}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
              <p className="text-[11.5px] text-[#9A9591]" data-testid="creative-generate-note">
                {autoCaps?.enabled
                  ? "Generates 4 finished creatives · Feed 4:5 + Story 9:16 · counts as 1 set"
                  : "Generates 3 variants · Feed 4:5 + Story 9:16 · counts as 1 set"}
              </p>
              <CtaButton onClick={() => (autoCaps?.enabled ? generateAuto() : generate())}
                disabled={!canGenerate || phase === "generating" || quotaLeft === 0 || busyAuto}
                data-testid={autoCaps?.enabled ? "creative-auto-generate-btn" : "creative-generate-btn"}>
                <Camera size={15} strokeWidth={1.8} />
                {autoCaps?.enabled ? "Shoot 4 creatives" : "Shoot 3 creatives"}
              </CtaButton>
            </div>
          </div>
        </div>
      </div>

      {phase === "generating" && !(autoSet && autoCaps?.enabled) && (
        <div className="card-soft mt-6 p-8 sm:p-10" data-testid="creative-generating-state">
          <MonoLabel>
            <span className="w-1.5 h-1.5 rounded-full bg-[#E0603A] inline-block animate-pulse" />
            ON SET
          </MonoLabel>
          <p className="mt-4 text-lg font-bold text-[#181621]">{stage || "Working…"}</p>
          <p className="text-[13.5px] text-[#54505B] mt-1">Brief → scenes → composite → typography. One to two minutes.</p>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/5] rounded-xl bg-[#F3EFE7] animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />)}
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="card-soft mt-6 p-8 flex flex-col items-center text-center" data-testid="creative-error-state">
          <div className="icon-tile !w-12 !h-12" style={{ background: "var(--amber-bg)" }}>
            <AlertTriangle size={22} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
          </div>
          <p className="mt-4 font-bold text-[15px] text-[#181621]">{errMsg}</p>
          <CtaButton className="mt-5 !py-2.5 !px-5 text-[13.5px]" onClick={() => generate()} disabled={!canGenerate} data-testid="creative-retry-btn">
            <RefreshCw size={14} strokeWidth={1.8} /> Try again
          </CtaButton>
        </div>
      )}

      {phase === "idle" && activeSet && (
        <div className="mt-10" data-testid="creative-results">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-bold text-[14.5px] text-[#181621] truncate max-w-[420px]">
              <span className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">Set</span>
              {activeSet.input?.offer?.slice(0, 48)}
            </p>
            {activeSet.input?.has_photo && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#BFDDB8] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#3E7B3E]">
                <BadgeCheck size={11} strokeWidth={2} /> Real product · untouched
              </span>
            )}
          </div>
          <GalleryErrorBoundary onReset={() => setActiveSet(null)}>
            <Gallery set={activeSet} quotaThirdsLeft={thirdsLeft} onRegen={regenVariant} onUndo={undoSet} busy={phase === "generating"} />
          </GalleryErrorBoundary>
          {activeSet.versions?.length > 0 ? (
            <RefineChat set={activeSet} onSend={refineSet} busy={phase === "generating"} />
          ) : (
            <p className="text-[11.5px] text-[#9A9591] mt-4" data-testid="creative-legacy-set-note">
              This set predates versioning — shoot a new set to unlock the refine chat and re-shoots.
            </p>
          )}
        </div>
      )}

      {autoSet && autoCaps?.enabled && (autoSet.set_id || phase === "generating") && (
        <div className="card-soft mt-6 p-6" data-testid="auto-set-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <MonoLabel>
              {(() => {
                const done = (autoSet.creatives || []).filter((c) => c.download_available).length;
                const total = autoSet.total || 4;
                if (phase === "generating") return `${done} OF ${total} CREATIVES READY`;
                return `${done} OF ${total} CREATIVES READY`;
              })()}
            </MonoLabel>
            {phase === "generating" && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#54505B]" data-testid="auto-set-progress-stage">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E0603A] inline-block animate-pulse" />
                {stage || "Working…"}
              </span>
            )}
          </div>
          <p className="text-[12.5px] text-[#54505B] mt-2" data-testid="auto-set-review-note">
            AI-generated creatives may contain text variations. Review every creative before publishing.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
            {(() => {
              const creatives = autoSet.creatives || [];
              const total = autoSet.total || 4;
              // Pad with skeleton slots while generation is still running.
              const slots = [...creatives];
              if (phase === "generating") {
                while (slots.length < total) slots.push({ concept_id: `pending_${slots.length + 1}`, _pending: true });
              }
              return slots.map((c, idx) => {
                if (c._pending) {
                  return (
                    <div key={c.concept_id} className="rounded-xl border border-[#EAE5DB] overflow-hidden bg-white"
                      data-testid={`auto-card-skeleton-${idx}`}>
                      <div className="w-full aspect-[4/5] bg-[#F3EFE7] animate-pulse flex items-center justify-center text-[11px] font-semibold text-[#9A9591]">
                        Building creative {idx + 1} of {total}…
                      </div>
                      <div className="p-3">
                        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#9A9591]">
                          Concept {idx + 1}
                        </span>
                        <div className="h-3 mt-2 bg-[#F3EFE7] rounded animate-pulse" />
                      </div>
                    </div>
                  );
                }
                const fmt = autoCardFmt[c.concept_id] || "feed";
                const availableFmts = c.formats && c.formats.length ? c.formats : (c.download_available ? ["feed"] : []);
                const imgClass = fmt === "story" ? "w-full aspect-[9/16] object-cover" : "w-full aspect-[4/5] object-cover";
                const ver = autoImgVer[c.concept_id] || 0;
                return (
                  <div key={c.concept_id} className="rounded-xl border border-[#EAE5DB] overflow-hidden bg-white"
                    data-testid={`auto-card-${c.concept_id}`}>
                    {c.download_available ? (
                      <img
                        src={`${API_BASE}/creative-studio/auto-image/${autoSet.set_id}/${c.concept_id}?fmt=${fmt}&v=${ver}`}
                        alt={c.concept_id}
                        className={imgClass}
                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        data-testid={`auto-card-img-${c.concept_id}`} />
                    ) : c.status === "failed" ? (
                      <div className="w-full aspect-[4/5] bg-[#F3EFE7] flex items-center justify-center text-[11px] text-[#9A9591]" data-testid={`auto-card-failed-${c.concept_id}`}>
                        Not produced
                      </div>
                    ) : (
                      <div className="w-full aspect-[4/5] bg-[#F3EFE7] animate-pulse flex items-center justify-center text-[11px] text-[#9A9591]">
                        Rendering…
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#9A9591]">
                          {c.concept_id.replace("concept_", "Concept ")}
                        </span>
                        {c.status && c.status !== "failed" && (
                          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.08em] bg-[#FFF3D6] text-[#8B6A1A] rounded-full px-2 py-0.5"
                            data-testid={`auto-card-review-${c.concept_id}`}>
                            Review recommended
                          </span>
                        )}
                      </div>
                      {availableFmts.length > 1 && (
                        <div className="inline-flex mt-2 rounded-md border border-[#EAE5DB] overflow-hidden">
                          {availableFmts.map((ft, i) => (
                            <button key={ft}
                              onClick={() => setAutoCardFmt((s) => ({ ...s, [c.concept_id]: ft }))}
                              className={`px-2 py-1 text-[10.5px] font-bold ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${fmt === ft ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
                              data-testid={`auto-card-fmt-${c.concept_id}-${ft}`}>
                              {ft === "feed" ? "Feed 4:5" : "Story 9:16"}
                            </button>
                          ))}
                        </div>
                      )}
                      {(c.quality?.issues || []).slice(0, 2).map((it, i) => (
                        <p key={i} className="text-[11px] text-[#54505B] mt-1"
                          data-testid={`auto-card-issue-${c.concept_id}-${i}`}>{it}</p>
                      ))}
                      {c.download_available && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <a href={`${API_BASE}/creative-studio/auto-image/${autoSet.set_id}/${c.concept_id}?fmt=${fmt}&v=${ver}`}
                            download className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#181621] hover:underline"
                            data-testid={`auto-card-download-${c.concept_id}`}>
                            <Download size={11} /> PNG
                          </a>
                          <button onClick={() => autoRegen(c.concept_id)}
                            disabled={!!autoCardBusy[c.concept_id] || phase === "generating"}
                            className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#54505B] hover:text-[#181621] disabled:opacity-40"
                            data-testid={`auto-card-regen-${c.concept_id}`}>
                            <RefreshCw size={11} /> Regenerate
                          </button>
                          <button onClick={() => autoUndoAction(c.concept_id)}
                            disabled={!!autoCardBusy[c.concept_id] || phase === "generating"}
                            className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#54505B] hover:text-[#181621] disabled:opacity-40"
                            data-testid={`auto-card-undo-${c.concept_id}`}>
                            <Undo2 size={11} /> Undo
                          </button>
                        </div>
                      )}
                      {c.download_available && phase !== "generating" && (
                        <div className="mt-2">
                          <input type="text" placeholder="Refine: shorter hook, warmer tone…"
                            value={autoRefineText[c.concept_id] || ""}
                            onChange={(e) => setAutoRefineText((s) => ({ ...s, [c.concept_id]: e.target.value }))}
                            disabled={!!autoCardBusy[c.concept_id]}
                            maxLength={500}
                            className="w-full text-[12px] border border-[#EAE5DB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#181621]"
                            data-testid={`auto-card-refine-input-${c.concept_id}`} />
                          <div className="flex items-center justify-between gap-2 mt-1.5">
                            <label className="inline-flex items-center gap-1.5 text-[10.5px] text-[#54505B] cursor-pointer">
                              <input type="checkbox" checked={!!autoRefineRemember[c.concept_id]}
                                onChange={(e) => setAutoRefineRemember((s) => ({ ...s, [c.concept_id]: e.target.checked }))}
                                disabled={!!autoCardBusy[c.concept_id]}
                                className="w-3 h-3 accent-[#181621]"
                                data-testid={`auto-card-remember-${c.concept_id}`} />
                              Remember this instruction for future outputs
                            </label>
                            <button onClick={() => autoRefine(c.concept_id)}
                              disabled={!!autoCardBusy[c.concept_id] || (autoRefineText[c.concept_id] || "").trim().length < 2}
                              className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#181621] hover:bg-[#000] disabled:opacity-40 rounded-md px-2 py-1"
                              data-testid={`auto-card-refine-send-${c.concept_id}`}>
                              <Sparkles size={10} /> Refine
                            </button>
                          </div>
                          {autoCardHint[c.concept_id] && (
                            <p className="text-[10.5px] text-[#181621] font-semibold mt-1"
                              data-testid={`auto-card-saved-${c.concept_id}`}>
                              {autoCardHint[c.concept_id]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {sets.length > 1 && phase === "idle" && (
        <div className="mt-10" data-testid="creative-history">
          <SectionLabel>History — last {sets.length}</SectionLabel>
          <div className="flex gap-3 mt-3 overflow-x-auto pb-2">
            {sets.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSet(s)}
                className={`shrink-0 rounded-xl border overflow-hidden transition-colors ${activeSet?.id === s.id ? "border-[#181621]" : "border-[#EAE5DB] hover:border-[#9A9591]"}`}
                data-testid={`history-set-${s.id}`}
              >
                <img src={imgUrl(s.id, 0, "feed")} alt={s.input?.offer} className="w-24 h-24 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
